import { Mail, ShieldCheck, Lock, Landmark, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS } from "@/constants";

const SOCIALS = [
  { label: "LinkedIn", href: "https://www.linkedin.com/" },
  { label: "X", href: "https://x.com/" },
  { label: "Instagram", href: "https://www.instagram.com/" },
];

const LEGAL = ["Privacy", "Terms", "Disclosures"];

const COLUMNS = [
  {
    title: "Platform",
    links: [
      { label: "AI Investment", href: "/#solutions" },
      { label: "Personalised advice", href: "/#solutions" },
      { label: "Real estate access", href: "/#solutions" },
      { label: "Tax-aware planning", href: "/#solutions" },
      { label: "Repatriation support", href: "/#solutions" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Who we are", href: "/#mission" },
      { label: "Why Zenstrust", href: "/#why" },
      { label: "Insights", href: "/blog" },
      { label: "FAQ", href: "/#faq" },
      { label: "Contact", href: "/#lead-form" },
    ],
  },
];

const ASSURANCES = [
  { icon: ShieldCheck, text: "SEBI-registered advisory partners" },
  { icon: Landmark, text: "RBI / FEMA compliant transfers" },
  { icon: Lock, text: "AES-256 at rest · TLS 1.3 in transit" },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[hsl(var(--zt-teal))] text-[hsl(var(--zt-warm-white))] relative overflow-hidden"
    >
      <div aria-hidden className="absolute inset-0 dot-grid-inverse opacity-25 pointer-events-none" />
      <div
        aria-hidden
        className="absolute bottom-[-20%] right-[-8%] w-[460px] h-[460px] rounded-full opacity-[0.12] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, #4F63F0 0%, transparent 65%)" }}
      />

      <div className="max-w-[1320px] mx-auto px-6 md:px-12 lg:px-16 py-16 lg:py-20 relative z-10">
        {/* CTA banner */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 lg:p-8 flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-16">
          <div>
            <h3 className="font-display text-[1.5rem] lg:text-[1.85rem] leading-tight text-white">
              Ready to put your India plan in motion?
            </h3>
            <p className="font-body text-[14px] text-white/60 mt-2 max-w-lg leading-relaxed">
              Book a 30-minute call with a cross-border advisor. No obligation, no sales script.
            </p>
          </div>
          <a
            href="/#lead-form"
            className="group inline-flex items-center gap-2 self-start bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/92 rounded-xl px-6 h-12 font-heading text-sm font-semibold tracking-tight transition-all duration-200 hover:-translate-y-0.5 flex-shrink-0"
          >
            Book a consultation
            <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="lg:col-span-4 space-y-5">
            <Logo
              size="h-16"
              to="/"
              src="/zenstrust-logo-white.png"
              className="transition-transform duration-300 hover:scale-[1.02]"
            />
            <p className="font-heading text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--zt-gold-soft))] font-bold">
              NRI investment solutions · Indian roots
            </p>
            <p className="text-[13.5px] text-white/60 max-w-sm leading-relaxed font-body">
              A cross-border wealth platform for the global Indian — AI-guided portfolios, registered advisors,
              and compliance handled end to end.
            </p>
            <div className="flex flex-wrap items-center gap-2 pt-2">
              {SOCIALS.map(({ label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${label.toLowerCase()}`}
                  className="rounded-lg border border-white/10 px-3 h-9 inline-flex items-center font-heading text-[12px] font-semibold text-white/70 hover:text-white hover:border-[hsl(var(--zt-gold))]/50 hover:bg-white/5 transition-all duration-200"
                >
                  {label}
                </a>
              ))}
              <a
                href="mailto:hello@zenstrust.com"
                aria-label="Email Zenstrust"
                data-testid="social-email"
                className="w-9 h-9 rounded-lg grid place-items-center border border-white/10 text-white/70 hover:text-white hover:border-[hsl(var(--zt-gold))]/50 hover:bg-white/5 transition-all duration-200"
              >
                <Mail size={15} />
              </a>
            </div>
          </div>

          {/* Link columns */}
          {COLUMNS.map((col) => (
            <nav key={col.title} className="lg:col-span-2 space-y-4">
              <h4 className="font-heading text-[11px] uppercase tracking-[0.16em] text-white/45 font-bold">
                {col.title}
              </h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      className="font-heading text-[13px] font-medium text-white/75 hover:text-[hsl(var(--zt-gold-soft))] transition-colors duration-200"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ))}

          {/* Quick nav + assurances */}
          <div className="lg:col-span-4 space-y-6">
            <div className="space-y-4">
              <h4 className="font-heading text-[11px] uppercase tracking-[0.16em] text-white/45 font-bold">
                Explore
              </h4>
              <ul className="flex flex-wrap gap-x-5 gap-y-3">
                {NAV_LINKS.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      className="font-heading text-[13px] font-medium text-white/75 hover:text-[hsl(var(--zt-gold-soft))] transition-colors duration-200"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
              {ASSURANCES.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2.5">
                  <Icon size={14} className="text-[hsl(var(--zt-sage))] flex-shrink-0" />
                  <span className="font-heading text-[12px] text-white/70 font-medium">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Disclaimer + legal */}
        <div className="mt-14 pt-7 border-t border-white/10 space-y-5">
          <p className="text-[11.5px] text-white/40 leading-relaxed font-body max-w-3xl">
            Investments are subject to market risk; past performance does not indicate future returns. Zenstrust
            does not take custody of client assets — funds move only through your own NRE/NRO accounts and
            securities are held with SEBI-regulated custodians in your name. This platform is a demonstration
            environment for client wealth-management flows.
          </p>
          <div className="flex flex-col md:flex-row justify-between gap-4 text-[12px] text-white/50 font-body">
            <span>© {new Date().getFullYear()} Zenstrust Pvt. Ltd. All rights reserved.</span>
            <div className="flex gap-7">
              {LEGAL.map((l) => (
                <a key={l} href="/coming-soon" className="hover:text-[hsl(var(--zt-gold-soft))] transition-colors">
                  {l}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
